import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowLeft, Ban, ChevronRight, Clock, Download, GitBranch, Hash,
  Loader2, Pause, PhoneCall, Play, RefreshCw, Repeat, RotateCcw, Route, Search,
  Square, Trash2, User, X,
} from 'lucide-react';
import { agents, ivrQa, phoneNumbers } from '../api';
import LoadingSpinner from './LoadingSpinner';

// ─────────────────────────────────────────────────────────────────────────────
// Chart colors
//
// Two series appear together anywhere in this view: recorded speech vs silence
// on the call-composition bar. Both steps were validated against this app's
// real surfaces (#ffffff light, #030711 dark) rather than eyeballed:
//
//   blue     #3484f4  contrast 3.65:1 light / 5.52:1 dark
//   amber700 #b45309  contrast 5.02:1 light / 4.01:1 dark
//   adjacent separation  deutan dE 30.8 · tritan dE 29.5 · normal dE 32.8
//
// Blue is the app's --primary token. Amber-700 is a darker step than the
// --warning token (#f59f0a), which fails the lightness band and lands at
// 2.13:1 on white - too pale for a large fill. The same two steps clear both
// surfaces, so one pair serves both themes.
//
// Green was rejected for the second series despite passing on paper: blue and
// #16a249 sit at tritan dE 5.3, which is indistinguishable for tritanopes.
const SPEECH_COLOR = '#3484f4';
const SILENCE_COLOR = '#b45309';

// ─────────────────────────────────────────────────────────────────────────────
// Formatting
// ─────────────────────────────────────────────────────────────────────────────

const fmtSeconds = (ms) => {
  if (ms === null || ms === undefined) return null;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s - m * 60)}s`;
};

const fmtSecondsOrDash = (ms) => fmtSeconds(ms) ?? '--';

/** Date and time, in the reader's own timezone.
 *
 *  A run is a campaign of calls placed minutes apart, and two runs against the
 *  same line on the same day are told apart by the clock - so the date alone
 *  was not enough to say which report you were looking at.
 *
 *  These columns are `datetime.utcnow()` and serialize without an offset, which
 *  a browser reads as local time - showing a call placed at 14:00 UTC as 14:00
 *  wherever you are, and mislabelling it by the whole offset. The webhook
 *  payload already stamps the same values with a Z, so the intent is settled;
 *  this says so on the way in rather than changing what the endpoints return. */
const fmtWhen = (value) => {
  if (!value) return null;
  const hasZone = typeof value === 'string' && /(Z|[+-]\d{2}:?\d{2})$/.test(value);
  const d = new Date(hasZone || typeof value !== 'string' ? value : `${value}Z`);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const OUTCOME_LABELS = {
  human_reached: 'Reached a person',
  branch_ended: 'Branch ended',
  loop_detected: 'Loop detected',
  max_turns: 'Hit the menu cap',
  max_duration: 'Hit the time cap',
  far_end_hung_up: 'Far end hung up',
  agent_ended: 'Agent ended it',
  stuck: 'Stuck on a menu',
  no_response: 'Rang out, no answer',
  no_answer: 'Never answered',
  menu_exhausted: 'Every option tried',
};

const outcomeLabel = (outcome) => OUTCOME_LABELS[outcome] || outcome || 'Unknown';

// ─────────────────────────────────────────────────────────────────────────────
// Figures
// ─────────────────────────────────────────────────────────────────────────────

/** Label + value, optionally with a note underneath. No plot, so no tooltip. */
const StatTile = ({ icon: Icon, label, value, note }) => (
  <div className="rounded-lg border border-border/50 bg-card/80 backdrop-blur-sm p-4">
    <div className="flex items-center gap-3">
      {Icon && (
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-muted/40 flex items-center justify-center">
          <Icon className="w-5 h-5 text-muted-foreground" />
        </div>
      )}
      <div className="min-w-0">
        {/* Proportional figures, not tabular: these are standalone display
            numbers, and equal-width digits read loose at this size. */}
        <div className="text-2xl font-semibold text-foreground leading-tight">{value}</div>
        <div className="text-sm text-muted-foreground">{label}</div>
        {note && <div className="text-xs text-muted-foreground mt-0.5">{note}</div>}
      </div>
    </div>
  </div>
);

/**
 * The number this whole feature exists to produce: the share of call time
 * callers spent listening to recordings rather than getting anywhere.
 * Exactly one hero per view.
 */
const HeroShare = ({ pct, recordingS, totalS }) => (
  <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
    <div className="text-sm text-muted-foreground">
      Share of call time spent listening to recordings
    </div>
    <div className="mt-2 text-6xl font-semibold text-foreground leading-none">
      {pct === null || pct === undefined ? '--' : `${pct}%`}
    </div>
    <div className="mt-3 text-sm text-muted-foreground">
      {recordingS !== null && totalS !== null
        ? `${fmtSecondsOrDash(recordingS * 1000)} of ${fmtSecondsOrDash(totalS * 1000)} across this run`
        : 'Not enough data yet'}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Charts
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Horizontal bars, one per call, for time to reach a person.
 *
 * Magnitude comparison, so one hue rather than a color per call: a call is not
 * an identity to tell apart, and a per-bar ramp would double-encode length as
 * lightness. Single series, so no legend box - the heading names what is
 * plotted. Only the extremes are labelled; the axis and the tooltip carry the
 * rest.
 */
const TimeToHumanChart = ({ calls, onSelect }) => {
  const withHuman = calls.filter((c) => c.time_to_human_ms !== null && c.time_to_human_ms !== undefined);
  if (!withHuman.length) {
    return (
      <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
        <h3 className="text-lg font-semibold text-foreground mb-1">Time to reach a person</h3>
        <p className="text-sm text-muted-foreground">
          No call in this run reached a person. That is itself a finding: every route
          tried ended in a recording, a loop, or a cap.
        </p>
      </div>
    );
  }

  const times = withHuman.map((c) => c.time_to_human_ms);
  const slowest = Math.max(...times);
  const fastest = Math.min(...times);
  // Round the axis up to a clean 15s step above the slowest call.
  const axisMax = Math.ceil(slowest / 15000) * 15000 || 15000;
  const ticks = [0, axisMax / 3, (axisMax * 2) / 3, axisMax];

  return (
    <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
      <h3 className="text-lg font-semibold text-foreground mb-1">Time to reach a person</h3>
      <p className="text-sm text-muted-foreground mb-5">
        {withHuman.length} of {calls.length} call{calls.length === 1 ? '' : 's'} got through to somebody
      </p>

      <div className="space-y-2">
        {withHuman.map((call) => {
          const pct = (call.time_to_human_ms / axisMax) * 100;
          const isExtreme = call.time_to_human_ms === slowest || call.time_to_human_ms === fastest;
          return (
            <button
              key={call.id}
              type="button"
              onClick={() => onSelect && onSelect(call)}
              title={`Call ${call.call_index}: ${fmtSecondsOrDash(call.time_to_human_ms)} to a person via ${(call.path || []).join(' → ') || 'no options'}`}
              className="w-full flex items-center gap-3 group text-left rounded-md hover:bg-muted/30 transition-colors py-1 px-1 -mx-1"
            >
              <span className="w-14 flex-shrink-0 text-xs text-muted-foreground tabular-nums">
                Call {call.call_index}
              </span>
              {/* Track is a lighter step of the same ramp, so state reads across
                  the whole bar. The bar itself is capped at 14px and rounds only
                  at the data end. */}
              <span className="flex-1 h-3.5 rounded-sm bg-muted/40 relative overflow-visible">
                <span
                  className="absolute left-0 top-0 h-full rounded-r-[4px]"
                  style={{ width: `${Math.max(pct, 0.8)}%`, backgroundColor: SPEECH_COLOR }}
                />
              </span>
              <span className="w-16 flex-shrink-0 text-xs text-right tabular-nums text-muted-foreground">
                {isExtreme ? fmtSecondsOrDash(call.time_to_human_ms) : ''}
              </span>
            </button>
          );
        })}
      </div>

      {/* Axis. Sized inside the card so the labels are never clipped. */}
      <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border/50">
        <span className="w-14 flex-shrink-0" />
        <span className="flex-1 flex justify-between text-xs text-muted-foreground tabular-nums">
          {ticks.map((t, i) => (
            <span key={i}>{fmtSecondsOrDash(t)}</span>
          ))}
        </span>
        <span className="w-16 flex-shrink-0" />
      </div>
    </div>
  );
};

/**
 * Part-to-whole: of the total time on this line, how much was a recording
 * talking versus the caller waiting on silence or hold.
 *
 * Two series, so a legend is always present. The 2px surface gap between the
 * segments does the separating - no border is drawn around either fill. An
 * in-segment label is only rendered when the segment is wide enough to hold it.
 */
const CompositionBar = ({ recordingMs, silenceMs, subtitle }) => {
  const total = (recordingMs || 0) + (silenceMs || 0);
  if (!total) return null;
  const recPct = (recordingMs / total) * 100;
  const silPct = 100 - recPct;

  return (
    <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
      <h3 className="text-lg font-semibold text-foreground mb-1">Where the time went</h3>
      <p className="text-sm text-muted-foreground mb-5">
        {subtitle || 'Split by what was happening on the line'}
      </p>

      <div className="flex w-full h-8 rounded-md overflow-hidden" role="img"
        aria-label={`Recordings talking ${recPct.toFixed(0)} percent, silence and hold ${silPct.toFixed(0)} percent`}>
        <div
          className="h-full flex items-center justify-center"
          style={{ width: `${recPct}%`, backgroundColor: SPEECH_COLOR }}
        >
          {/* Only label inside the fill when it comfortably fits. White is
              chosen against both fills' luminance so it always clears. */}
          {recPct >= 18 && (
            <span className="text-xs font-medium text-white tabular-nums px-2">
              {recPct.toFixed(0)}%
            </span>
          )}
        </div>
        {/* The surface gap: 2px of card color, not a stroke. */}
        <div className="h-full flex-shrink-0 bg-card" style={{ width: 2 }} />
        <div
          className="h-full flex items-center justify-center"
          style={{ width: `calc(${silPct}% - 2px)`, backgroundColor: SILENCE_COLOR }}
        >
          {silPct >= 18 && (
            <span className="text-xs font-medium text-white tabular-nums px-2">
              {silPct.toFixed(0)}%
            </span>
          )}
        </div>
      </div>

      {/* Legend: always present at two or more series, so identity is never
          carried by color alone. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4">
        <span className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: SPEECH_COLOR }} />
          Recordings talking
          <span className="text-foreground tabular-nums">{fmtSecondsOrDash(recordingMs)}</span>
        </span>
        <span className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: SILENCE_COLOR }} />
          Silence and hold
          <span className="text-foreground tabular-nums">{fmtSecondsOrDash(silenceMs)}</span>
        </span>
      </div>
    </div>
  );
};

/**
 * How the calls in this run ended.
 *
 * One hue on a magnitude comparison rather than a color per outcome: seven
 * outcomes would need seven categorical hues, which is at the token ceiling and
 * buries the point. The names are the identity channel.
 */
const OutcomeBreakdown = ({ outcomes, total }) => {
  const rows = Object.entries(outcomes || {}).sort((a, b) => b[1] - a[1]);
  if (!rows.length) return null;
  const max = Math.max(...rows.map(([, n]) => n));

  return (
    <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
      <h3 className="text-lg font-semibold text-foreground mb-1">How the calls ended</h3>
      <p className="text-sm text-muted-foreground mb-5">Which stopping condition fired</p>
      <div className="space-y-2.5">
        {rows.map(([outcome, count]) => (
          <div key={outcome} className="flex items-center gap-3">
            <span className="w-40 flex-shrink-0 text-sm text-muted-foreground truncate"
              title={outcomeLabel(outcome)}>
              {outcomeLabel(outcome)}
            </span>
            <span className="flex-1 h-3.5 rounded-sm bg-muted/40 relative">
              <span
                className="absolute left-0 top-0 h-full rounded-r-[4px]"
                style={{ width: `${(count / max) * 100}%`, backgroundColor: SPEECH_COLOR }}
              />
            </span>
            <span className="w-20 flex-shrink-0 text-xs text-right text-muted-foreground tabular-nums">
              {count} of {total}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * The findings an operator should see. `duplicate_press` is the agent's own
 * bookkeeping - the same key sent twice with nothing heard in between, kept so
 * the route stays honest - and says nothing about the client's phone tree, so
 * it is left out of every count and list here. The record still holds it.
 */
const HIDDEN_ISSUE_TYPES = new Set(['duplicate_press']);
const visibleIssues = (call) =>
  (call?.issues || []).filter((issue) => !HIDDEN_ISSUE_TYPES.has(issue?.type));

/**
 * Loops and stale announcements, quoted.
 *
 * The wording matters more than the count: "your IVR loops" is a complaint,
 * "option 3 under billing sends the caller back to the main menu, and this
 * recording still mentions COVID" is something a client can act on. Each
 * finding carries an icon and a text label, so severity never rests on color.
 */
const FindingsPanel = ({ calls }) => {
  const findings = [];
  (calls || []).forEach((call) => {
    visibleIssues(call).forEach((issue) => {
      findings.push({ ...issue, callIndex: call.call_index, callId: call.id });
    });
  });

  if (!findings.length) {
    return (
      <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
        <h3 className="text-lg font-semibold text-foreground mb-1">Issues found</h3>
        <p className="text-sm text-muted-foreground">
          Nothing flagged yet. Loops, refused keys and stale announcements appear here as they turn up.
        </p>
      </div>
    );
  }

  const loops = findings.filter((f) => f.type === 'loop');
  const stale = findings.filter((f) => f.type === 'stale_announcement');
  const refused = findings.filter((f) => f.type === 'rejected_press');
  const other = findings.filter(
    (f) => f.type !== 'loop' && f.type !== 'stale_announcement' && f.type !== 'rejected_press'
  );

  return (
    <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
      <h3 className="text-lg font-semibold text-foreground mb-1">Issues found</h3>
      <p className="text-sm text-muted-foreground mb-5">
        {findings.length} finding{findings.length === 1 ? '' : 's'} across this run
      </p>

      <div className="space-y-3">
        {loops.map((f, i) => (
          <div key={`loop-${i}`} className="flex gap-3 p-4 rounded-lg bg-background/30 border border-border/40">
            <Repeat className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'hsl(var(--destructive))' }} />
            <div className="min-w-0">
              <div className="text-sm font-medium text-foreground">
                Loop on call {f.callIndex}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Menu {f.at_turn} sent the caller back to menu {f.loops_back_to_turn}
                {f.similarity ? ` (${Math.round(f.similarity * 100)}% the same wording)` : ''}
              </div>
              {f.prompt_text && (
                <div className="mt-2 text-xs text-muted-foreground italic border-l-2 border-border/60 pl-3">
                  "{f.prompt_text}"
                </div>
              )}
            </div>
          </div>
        ))}

        {stale.map((f, i) => (
          <div key={`stale-${i}`} className="flex gap-3 p-4 rounded-lg bg-background/30 border border-border/40">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: SILENCE_COLOR }} />
            <div className="min-w-0">
              <div className="text-sm font-medium text-foreground">
                Announcement looks out of date, call {f.callIndex}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">Heard at menu {f.at_turn}</div>
              {f.wording && (
                <div className="mt-2 text-xs text-muted-foreground italic border-l-2 border-border/60 pl-3">
                  "{f.wording}"
                </div>
              )}
            </div>
          </div>
        ))}

        {refused.map((f, i) => (
          <div key={`refused-${i}`} className="flex gap-3 p-4 rounded-lg bg-background/30 border border-border/40">
            <Ban className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'hsl(var(--destructive))' }} />
            <div className="min-w-0">
              <div className="text-sm font-medium text-foreground">
                Key {f.digit}{f.label ? ` (${f.label})` : ''} refused, call {f.callIndex}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Menu {f.at_turn}
                {f.think_time_ms != null ? ` · pressed ${fmtSecondsOrDash(f.think_time_ms)} after the menu stopped` : ''}
              </div>
              {f.reason && (
                <div className="mt-2 text-xs text-muted-foreground border-l-2 border-border/60 pl-3">
                  {f.reason}
                </div>
              )}
            </div>
          </div>
        ))}

        {other.map((f, i) => (
          <div key={`other-${i}`} className="flex gap-3 p-4 rounded-lg bg-background/30 border border-border/40">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-muted-foreground" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-foreground">
                {f.type} on call {f.callIndex}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">Menu {f.at_turn}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * The discovered map of the line, as an indented tree.
 *
 * Built as a list rather than a node-link graph on purpose. The question this
 * view answers is "which option has nobody pressed yet", and a list answers it
 * with real text at every depth - which also means it survives a screen reader,
 * a narrow window and both themes, none of which a positioned graph does for
 * free. `reactflow` is already a dependency if a spatial view is wanted later.
 *
 * Every state carries an icon and a word, so none of it rests on color.
 */
const TreeView = ({ tree, loading }) => {
  if (loading) {
    return (
      <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
        <LoadingSpinner size="sm" text="Loading the map..." />
      </div>
    );
  }
  if (!tree || !tree.nodes || !tree.nodes.length) {
    return (
      <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
        <h3 className="text-lg font-semibold text-foreground mb-1">The phone tree</h3>
        <p className="text-sm text-muted-foreground">
          Nothing mapped yet. The map builds itself as calls come in.
        </p>
      </div>
    );
  }

  const sorted = [...tree.nodes].sort((a, b) => {
    if (a.depth !== b.depth) return a.depth - b.depth;
    return (a.path_prefix || []).join().localeCompare((b.path_prefix || []).join());
  });

  return (
    <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
      <div className="flex items-start justify-between gap-4 mb-1">
        <h3 className="text-lg font-semibold text-foreground">The phone tree</h3>
        <div className="text-right flex-shrink-0">
          <div className="text-2xl font-semibold text-foreground leading-tight">
            {tree.coverage_pct}%
          </div>
          <div className="text-xs text-muted-foreground">explored</div>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-1">
        {tree.node_count} menu{tree.node_count === 1 ? '' : 's'} found, {tree.max_depth + 1} level
        {tree.max_depth === 0 ? '' : 's'} deep, {tree.options_taken} of {tree.options_found} announced
        options taken
      </p>
      {tree.coverage_is_floor && (
        <p className="text-xs text-muted-foreground mb-5">
          Coverage is a floor, not an estimate: options the agent never heard, because a
          keypress cut a menu short, are not counted in the total.
        </p>
      )}

      <div className="space-y-2">
        {sorted.map((node) => {
          const taken = new Set((node.options_taken || []).map(String));
          const failedHere = new Map((node.failed || []).map((f) => [String(f.digit), f.reasons || []]));
          return (
            <div
              key={node.id}
              className="p-3 rounded-lg bg-background/30 border border-border/40"
              style={{ marginLeft: Math.min(node.depth, 5) * 20 }}
            >
              <div className="flex items-start gap-2">
                <GitBranch className="w-3.5 h-3.5 flex-shrink-0 mt-1 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {(node.path_prefix || []).length
                        ? (node.path_prefix || []).join(' → ')
                        : 'first menu'}
                    </span>
                    {node.reaches_human && (
                      <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded"
                        style={{ color: 'hsl(var(--success))', backgroundColor: 'hsl(var(--success) / 0.12)' }}>
                        <User className="w-3 h-3" /> reaches a person
                      </span>
                    )}
                    {node.is_terminal && (
                      <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-muted/50 text-muted-foreground">
                        <X className="w-3 h-3" /> dead end
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground tabular-nums">
                      heard {node.visit_count}x
                      {node.avg_speech_ms ? ` · talks for ${fmtSecondsOrDash(node.avg_speech_ms)}` : ''}
                    </span>
                  </div>

                  <div className="text-sm text-foreground mt-1 line-clamp-2">
                    {node.prompt_text || <span className="text-muted-foreground">(nothing transcribed)</span>}
                  </div>

                  {(node.options_offered || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {node.options_offered.map((opt) => {
                        const isTaken = taken.has(String(opt.digit));
                        const failedReasons = failedHere.get(String(opt.digit));
                        if (!isTaken && failedReasons) {
                          return (
                            <span
                              key={opt.digit}
                              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border"
                              style={{ borderColor: 'hsl(var(--destructive))', color: 'hsl(var(--destructive))' }}
                              title={failedReasons.join('\n')}
                            >
                              <Ban className="w-3 h-3" />
                              <span className="tabular-nums font-medium">{opt.digit}</span>
                              {opt.label ? <span className="truncate max-w-[12rem]">{opt.label}</span> : null}
                              <span className="opacity-70">failed, not retried</span>
                            </span>
                          );
                        }
                        return (
                          <span
                            key={opt.digit}
                            className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${
                              isTaken
                                ? 'border-border/60 bg-muted/40 text-muted-foreground'
                                : 'border-dashed'
                            }`}
                            style={
                              isTaken
                                ? undefined
                                : { borderColor: SILENCE_COLOR, color: SILENCE_COLOR }
                            }
                            title={isTaken ? 'Already explored' : 'Nobody has pressed this yet'}
                          >
                            <span className="tabular-nums font-medium">{opt.digit}</span>
                            {opt.label ? <span className="truncate max-w-[12rem]">{opt.label}</span> : null}
                            {!isTaken && <span className="opacity-70">unexplored</span>}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {(tree.failed_routes || []).length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-semibold text-foreground mb-1">Failed routes, not retried</h4>
          <p className="text-xs text-muted-foreground mb-3">
            Each of these was tried and got nowhere. The planner does not send another call down
            them, so what went wrong is recorded here as the finding.
          </p>
          <div className="space-y-2">
            {tree.failed_routes.map((r) => (
              <div key={r.route.join('>')} className="flex gap-3 p-3 rounded-lg bg-background/30 border border-border/40">
                <Ban className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'hsl(var(--destructive))' }} />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground tabular-nums">
                    {r.route.join(' → ')}
                    {r.label ? <span className="font-normal text-muted-foreground"> · {r.label}</span> : null}
                  </div>
                  {r.menu_text && (
                    <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                      at "{r.menu_text}"
                    </div>
                  )}
                  <ul className="mt-2 space-y-1 text-xs text-muted-foreground border-l-2 border-border/60 pl-3">
                    {(r.reasons || []).map((reason, i) => (
                      <li key={i}>{reason}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Call detail: turn by turn
// ─────────────────────────────────────────────────────────────────────────────

const CallDetail = ({ callId, onBack }) => {
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ivrQa.getCall(callId)
      .then((res) => { if (!cancelled) { setCall(res.data); setError(null); } })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.detail || 'Could not load this call'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [callId]);

  if (loading) return <LoadingSpinner size="lg" text="Loading the call..." />;
  if (error) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Back to the run
        </button>
        <div className="rounded-lg border border-border/60 bg-card/80 p-6 text-sm text-muted-foreground">{error}</div>
      </div>
    );
  }

  const turns = call.turns || [];

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> Back to the run
      </button>

      <div>
        <h2 className="text-2xl font-semibold text-foreground">Call {call.call_index}</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {(call.path || []).length ? (call.path || []).join(' → ') : 'No options taken'}
          {' · '}{outcomeLabel(call.outcome)}
          {call.created_at ? ` · ${fmtWhen(call.created_at)}` : ''}
        </p>
        {/* The label names the condition that fired; this is what happened.
            Printed at full width rather than as a tooltip, because it is the
            line an operator reads before anything else on the page. */}
        {call.end_reason && (
          <p className="text-sm text-foreground mt-2 max-w-3xl">{call.end_reason}</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        <StatTile icon={User} label="Time to a person"
          value={call.time_to_human_ms !== null ? fmtSecondsOrDash(call.time_to_human_ms) : 'Never'}
          note={call.time_to_human_ms === null ? 'No person was reached' : undefined} />
        {/* Its own tile, not a footnote on call length. How long a caller
            holds before the line says anything is a finding about the line,
            and it was previously legible only as small print. */}
        <StatTile icon={PhoneCall} label="Rang before answer"
          value={call.ring_to_answer_ms !== null && call.ring_to_answer_ms !== undefined
            ? fmtSecondsOrDash(call.ring_to_answer_ms) : '--'}
          note={call.ring_to_answer_ms !== null && call.ring_to_answer_ms !== undefined
            ? 'before the line said a word' : 'the line never answered'} />
        <StatTile icon={Clock} label="Call length" value={fmtSecondsOrDash(call.total_duration_ms)}
          note="from the answer, ring excluded" />
        <StatTile icon={Hash} label="Menus heard" value={call.turn_count ?? turns.length} />
        <StatTile icon={AlertTriangle} label="Issues" value={visibleIssues(call).length} />
      </div>

      <CompositionBar recordingMs={call.ivr_speech_ms_total} silenceMs={call.dead_air_ms_total}
        subtitle="Every second of this call, split by what was happening" />

      {call.recording_url && (
        <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Play className="w-5 h-5" /> Recording
          </h3>
          <audio controls src={call.recording_url} className="w-full">
            <track kind="captions" />
          </audio>
        </div>
      )}

      {/* The turn table. This is also the table view for the charts above -
          every number plotted anywhere is reachable here as text. */}
      <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
        <h3 className="text-lg font-semibold text-foreground mb-1">Menu by menu</h3>
        <p className="text-sm text-muted-foreground mb-5">
          Every node this call landed on, what it said, and what it cost
          {call.recording_url ? '. The times are offsets into the recording above, so you can scrub to any row' : ''}
        </p>
        {!turns.length ? (
          <p className="text-sm text-muted-foreground">No menus were recorded on this call.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border/60">
                  <th className="pb-2 pr-3 font-medium">At</th>
                  <th className="pb-2 pr-3 font-medium">What it said</th>
                  <th className="pb-2 pr-3 font-medium">Options offered</th>
                  <th className="pb-2 pr-3 font-medium">Pressed</th>
                  <th className="pb-2 pr-3 font-medium text-right">Talking</th>
                  <th className="pb-2 font-medium text-right">Silence</th>
                </tr>
              </thead>
              <tbody>
                {turns.map((t) => (
                  <tr key={t.turn_index} className="border-b border-border/40 last:border-0 align-top">
                    <td className="py-3 pr-3 text-muted-foreground tabular-nums whitespace-nowrap">
                      {fmtSecondsOrDash(t.at_ms)}
                    </td>
                    <td className="py-3 pr-3 text-foreground max-w-md">
                      {t.prompt_text || <span className="text-muted-foreground">(nothing transcribed)</span>}
                      {t.classification && (
                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-muted/50 text-muted-foreground whitespace-nowrap">
                          {t.classification.replace(/_/g, ' ')}
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-3">
                      {(t.options_offered || []).length ? (
                        <div className="flex flex-wrap gap-1">
                          {t.options_offered.map((o) => (
                            <span key={o.digit} className="text-xs px-1.5 py-0.5 rounded bg-muted/40 text-muted-foreground whitespace-nowrap">
                              <span className="tabular-nums font-medium">{o.digit}</span>
                              {o.label ? ` ${o.label}` : ''}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">none announced</span>
                      )}
                    </td>
                    <td className="py-3 pr-3 tabular-nums text-foreground">
                      {t.input_sent || <span className="text-muted-foreground">--</span>}
                    </td>
                    <td className="py-3 pr-3 text-right tabular-nums text-muted-foreground whitespace-nowrap">
                      {fmtSecondsOrDash(t.ivr_speech_ms)}
                    </td>
                    <td className="py-3 text-right tabular-nums text-muted-foreground whitespace-nowrap">
                      {fmtSecondsOrDash(t.dead_air_ms)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {Object.keys(call.invented_values || {}).length > 0 && (
        <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
          <h3 className="text-lg font-semibold text-foreground mb-1">Details the agent made up</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Generated before the call and reused throughout it, so the phone tree could not
            reject the agent and send it round a loop
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {Object.entries(call.invented_values).map(([k, v]) => (
              <div key={k} className="text-sm p-2 rounded bg-background/30 border border-border/40">
                <span className="text-muted-foreground">{k.replace(/_/g, ' ')}: </span>
                <span className="text-foreground tabular-nums">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Run detail
// ─────────────────────────────────────────────────────────────────────────────

// Delete is two different actions wearing one word, and the prompt is the only
// place the difference is visible. Deleting a run keeps the menu map those
// calls paid for, so the next call resumes into the unexplored branches;
// resetting the line is the only thing that starts over from the root. Pick the
// wrong one and you lose either a report or the whole accumulated tree, so the
// prompt spells out what survives and the reset asks a second time.
const confirmAndDelete = (run, scope) => {
  const name = run.label || run.target_number;
  const calls = run.completed_calls || 0;
  const callWord = `${calls} call${calls === 1 ? '' : 's'}`;

  // Force reset: the same reset, after stopping everything still dialling the
  // number. It hangs up on live calls, so it gets its own wording and its own
  // second check.
  if (scope === 'force') {
    const forceMessage = [
      `Force reset ${run.target_number}?`,
      '',
      'This stops every dialler on this number, cancels the calls still waiting',
      'to go out, and hangs up any call on the line right now. Then it deletes',
      'every run, every call and the whole explored menu map, exactly like',
      '"Reset line".',
      '',
      'Calls it hangs up are not recorded. Their recordings and transcripts in',
      'conversation history are kept.',
      '',
      'This cannot be undone.',
    ].join('\n');
    if (!window.confirm(forceMessage)) return Promise.resolve(null);
    if (!window.confirm(
      `Last check: hang up all calls to ${run.target_number} and delete its explored map?`)) {
      return Promise.resolve(null);
    }
    return ivrQa.deleteRun(run.id, 'line', true).then((res) => res.data);
  }

  const message = scope === 'line'
    ? [
      `Reset everything for ${run.target_number}?`,
      '',
      'This deletes every run against this number, every call recorded against',
      'it, and the whole explored menu map. The next call starts from the root',
      'with no steering and no history.',
      '',
      'Call recordings and transcripts in conversation history are NOT deleted.',
      '',
      'This cannot be undone.',
    ].join('\n')
    : [
      `Delete the run "${name}"?`,
      '',
      `This removes the run and its ${callWord}, with their per-menu timings.`,
      '',
      'The explored menu map for this number is KEPT, so the next call still',
      'resumes into the unexplored branches. To start over from the root, use',
      '"Reset line" instead.',
      '',
      'This cannot be undone.',
    ].join('\n');

  if (!window.confirm(message)) return Promise.resolve(null);

  // Second gate on the destructive one only. The map is the expensive artifact:
  // it is the accumulated result of every call ever placed against this line.
  if (scope === 'line' && !window.confirm(
    `Last check: permanently delete the explored map for ${run.target_number}?`)) {
    return Promise.resolve(null);
  }

  return ivrQa.deleteRun(run.id, scope).then((res) => res.data);
};

// ─────────────────────────────────────────────────────────────────────────────
// The dialler (Stage 5)
//
// A run used to be a label to group calls placed by hand. It can now place them
// itself, and this is the surface for that: how many, from which caller ID, how
// far apart, and how many at once.
//
// Two things about the wording are deliberate. "Caller ID" is presented as a
// choice of agent as well as a number, because on an outbound call the caller
// ID is what decides which agent runs - pick a number belonging to a different
// agent and the calls navigate but are never audited, which is invisible
// afterwards. And the state is named for what it is doing ("Dialling", "Paused")
// rather than for the field it comes from, since an operator watching a
// campaign is asking whether calls are going out right now.
// ─────────────────────────────────────────────────────────────────────────────

const DIALLER_LABELS = {
  idle: 'Not dialling',
  running: 'Dialling',
  paused: 'Paused',
  stopping: 'Finishing the last call',
  stopped: 'Stopped',
  finished: 'Finished',
  failed: 'Stopped on an error',
};

const DIALLER_ACTIVE = ['running', 'paused', 'stopping'];

/** Caller IDs the org holds, each with the agent an outbound call would run as. */
const useCallerIds = () => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    Promise.all([phoneNumbers.list(), agents.list()])
      .then(([numbersRes, agentsRes]) => {
        if (!live) return;
        const byId = {};
        (agentsRes.data || []).forEach((a) => { byId[a.id] = a; });
        setOptions((numbersRes.data || []).map((n) => {
          const agent = byId[n.agent_id];
          return {
            e164: n.e164,
            agentName: n.agent_name || agent?.name || 'no agent',
            // The same three toggles the voice worker gates on. A number whose
            // agent fails any of them can still be picked, and the API will say
            // why - but flagging it here saves the round trip.
            qaReady: !!(agent && agent.ivr_qa_mode && agent.dtmf_enabled
                        && agent.dtmf_ivr_detection && agent.is_active !== false),
          };
        }));
      })
      .catch(() => { if (live) setOptions([]); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  return { options, loading };
};

/** The campaign settings. Shared by the create form and the dialler panel. */
const DiallerSettings = ({ value, onChange, callerIds, disabled }) => {
  const set = (key) => (e) => onChange({ ...value, [key]: e.target.value });
  const chosen = callerIds.find((c) => c.e164 === value.from_number);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div>
          <label className="text-sm font-medium text-foreground">Calls to place</label>
          <input type="number" min="1" max="500" value={value.planned_calls}
            onChange={set('planned_calls')} disabled={disabled}
            className="mt-1 w-full px-3 py-2 text-sm tabular-nums bg-background border border-border/60 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50" />
          <p className="text-xs text-muted-foreground mt-1">Each call maps a bit more of the tree</p>
        </div>
        <div>
          <label className="text-sm font-medium text-foreground">Caller ID</label>
          <select value={value.from_number} onChange={set('from_number')} disabled={disabled}
            className="mt-1 w-full px-3 py-2 text-sm bg-background border border-border/60 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50">
            <option value="">Pick a number</option>
            {callerIds.map((c) => (
              <option key={c.e164} value={c.e164}>
                {c.e164} - {c.agentName}{c.qaReady ? '' : ' (no IVR QA)'}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            {chosen && !chosen.qaReady
              ? `${chosen.agentName} would answer these calls without auditing them`
              : 'The caller ID decides which agent runs the call'}
          </p>
        </div>
        <div>
          <label className="text-sm font-medium text-foreground">Gap between calls</label>
          <input type="number" min="5" max="3600" value={value.pacing_seconds}
            onChange={set('pacing_seconds')} disabled={disabled}
            className="mt-1 w-full px-3 py-2 text-sm tabular-nums bg-background border border-border/60 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50" />
          <p className="text-xs text-muted-foreground mt-1">Seconds of quiet after each call ends</p>
        </div>
        <div>
          <label className="text-sm font-medium text-foreground">At once</label>
          <select value={value.max_concurrency} onChange={set('max_concurrency')} disabled={disabled}
            className="mt-1 w-full px-3 py-2 text-sm bg-background border border-border/60 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50">
            <option value="1">One call at a time</option>
            <option value="2">Two calls at a time</option>
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            {String(value.max_concurrency) === '2'
              ? 'Faster, but two calls can pick the same branch'
              : 'Every call is steered by everything learned before it'}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Progress across the campaign. One hue, because this is a magnitude and the
 * three figures are the same thing measured at different stages.
 */
const DiallerProgress = ({ run }) => {
  const planned = run.planned_calls || 0;
  const attempted = run.attempted_calls || 0;
  const recorded = run.completed_calls || 0;
  const pct = planned ? Math.min(100, Math.round((attempted / planned) * 100)) : 0;

  return (
    <div>
      <div className="flex items-baseline justify-between gap-4 mb-2">
        <div className="text-sm text-foreground">
          <span className="tabular-nums font-medium">{attempted}</span>
          <span className="text-muted-foreground"> of </span>
          <span className="tabular-nums">{planned || '--'}</span>
          <span className="text-muted-foreground"> calls placed</span>
        </div>
        <div className="text-xs text-muted-foreground tabular-nums">
          {recorded} audited
          {run.failed_calls ? ` · ${run.failed_calls} never connected` : ''}
        </div>
      </div>
      <div className="h-2 w-full rounded-full bg-muted/40 overflow-hidden"
        role="progressbar" aria-valuenow={attempted} aria-valuemin={0} aria-valuemax={planned || undefined}>
        <div className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: SPEECH_COLOR }} />
      </div>
    </div>
  );
};

const DiallerPanel = ({ run, error, onChanged, onError }) => {
  const state = run.orchestrator_status || 'idle';
  const active = DIALLER_ACTIVE.includes(state);
  const { options: callerIds } = useCallerIds();
  const [busy, setBusy] = useState(null);
  const [settings, setSettings] = useState({
    planned_calls: run.planned_calls || 10,
    from_number: run.from_number || '',
    pacing_seconds: run.pacing_seconds || 90,
    max_concurrency: String(run.max_concurrency || 1),
  });

  // Only default the caller ID once, and only to a number that would actually
  // audit: pre-filling a non-QA number would make the refusal look arbitrary.
  useEffect(() => {
    if (settings.from_number || !callerIds.length) return;
    const ready = callerIds.find((c) => c.qaReady);
    if (ready) setSettings((s) => ({ ...s, from_number: ready.e164 }));
  }, [callerIds, settings.from_number]);

  const act = (label, fn) => {
    setBusy(label);
    onError(null);
    fn()
      .then((res) => onChanged(res.data))
      .catch((err) => onError(err.response?.data?.detail || 'The dialler would not start'))
      .finally(() => setBusy(null));
  };

  const start = () => act('start', () => ivrQa.startRun(run.id, {
    planned_calls: Number(settings.planned_calls) || 0,
    from_number: settings.from_number || null,
    pacing_seconds: Number(settings.pacing_seconds) || 90,
    max_concurrency: Number(settings.max_concurrency) || 1,
  }));

  return (
    <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Dialler</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Place the calls for this run, spaced out, and stop when the campaign is done.
            Every call is steered away from the branches earlier calls already walked.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-xs px-2 py-1 rounded ${
            state === 'running' ? 'bg-primary/15 text-primary'
              : state === 'failed' ? 'bg-destructive/10 text-destructive'
              : 'bg-muted/50 text-muted-foreground'
          }`}>
            {DIALLER_LABELS[state] || state}
          </span>
          {state === 'running' && run.next_call_in_s !== null && run.next_call_in_s !== undefined && (
            <span className="text-xs text-muted-foreground tabular-nums">
              next call in {run.next_call_in_s}s
            </span>
          )}
          {run.calls_in_flight > 0 && (
            <span className="text-xs text-muted-foreground tabular-nums">
              {run.calls_in_flight} on a call
            </span>
          )}
        </div>
      </div>

      {(run.planned_calls || run.attempted_calls) ? <DiallerProgress run={run} /> : null}

      {/* Two different errors, and they are not the same thing. `error` is why
          the last click was refused, usually a caller ID that would not audit.
          `last_error` is why the dialler itself stopped. */}
      {(error || run.last_error) && (
        <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-foreground">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-destructive" />
          <span>{error || run.last_error}</span>
        </div>
      )}

      {/* A run marked as dialling that nothing is driving. The server restarts
          its diallers on the way up, so this means the resume did not take -
          worth saying plainly, since the run looks busy and is not. */}
      {state === 'running' && !run.dialler_live && (
        <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            This run is marked as dialling but nothing is driving it. Resume it to carry
            on from call {(run.attempted_calls || 0) + 1}.
          </span>
        </div>
      )}

      {active ? (
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-sm text-muted-foreground">
            {run.from_number ? `Dialling ${run.target_number} from ${run.from_number}` : null}
            {run.pacing_seconds ? `, ${run.pacing_seconds}s apart` : null}
            {Number(run.max_concurrency) === 2 ? ', two at a time' : null}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {state === 'running' && (
              <button onClick={() => act('pause', () => ivrQa.pauseRun(run.id))} disabled={!!busy}
                className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors disabled:opacity-50">
                {busy === 'pause' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pause className="w-4 h-4" />}
                Pause
              </button>
            )}
            {(state === 'paused' || (state === 'running' && !run.dialler_live)) && (
              <button onClick={() => act('resume', () => ivrQa.resumeRun(run.id))} disabled={!!busy}
                className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-gradient-button text-primary-foreground shadow-md hover:opacity-90 transition-opacity disabled:opacity-50">
                {busy === 'resume' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Resume
              </button>
            )}
            <button onClick={() => act('stop', () => ivrQa.stopRun(run.id))}
              disabled={!!busy || state === 'stopping'}
              title="Stop placing calls. A call already in progress is left to finish and is still recorded."
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-border/60 text-muted-foreground hover:text-destructive hover:border-destructive/40 hover:bg-destructive/5 transition-colors disabled:opacity-50">
              {busy === 'stop' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
              Stop
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <DiallerSettings value={settings} onChange={setSettings}
            callerIds={callerIds} disabled={!!busy} />
          <div className="flex items-center gap-3">
            <button onClick={start} disabled={!!busy || !Number(settings.planned_calls)}
              className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-gradient-button text-primary-foreground shadow-md hover:opacity-90 transition-opacity disabled:opacity-50">
              {busy === 'start' ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />}
              {run.attempted_calls ? 'Keep dialling' : 'Start dialling'}
            </button>
            {run.attempted_calls > 0 && (
              <span className="text-sm text-muted-foreground">
                {run.attempted_calls} call{run.attempted_calls === 1 ? '' : 's'} already placed on this run
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


const RunDetail = ({ runId, onBack, onSelectCall, onDeleted }) => {
  const [run, setRun] = useState(null);
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [treeLoading, setTreeLoading] = useState(true);
  const [error, setError] = useState(null);
  // Kept apart from `error`, which replaces the whole view: a refused Start is
  // a message beside the button, not a reason to hide the report.
  const [diallerError, setDiallerError] = useState(null);
  const [deleting, setDeleting] = useState(null);
  // Set when a plain reset was refused for a dialler this page cannot see.
  const [forceOffered, setForceOffered] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    ivrQa.getRun(runId)
      .then((res) => {
        setRun(res.data);
        setError(null);
        setTreeLoading(true);
        return ivrQa.getTree(res.data.target_number)
          .then((t) => setTree(t.data))
          .catch(() => setTree(null))
          .finally(() => setTreeLoading(false));
      })
      .catch((err) => setError(err.response?.data?.detail || 'Could not load this run'))
      .finally(() => setLoading(false));
  }, [runId]);

  useEffect(() => { load(); }, [load]);

  // While the dialler is working, the numbers on this page change without
  // anyone touching it. The full reload refetches the tree as well, which is
  // the point: a call landing is exactly when the map grows.
  const diallerActive = DIALLER_ACTIVE.includes(run?.orchestrator_status);
  useEffect(() => {
    if (!diallerActive) return undefined;
    const timer = setInterval(load, 8000);
    return () => clearInterval(timer);
  }, [diallerActive, load]);

  const remove = (scope) => {
    setDeleting(scope);
    confirmAndDelete(run, scope)
      .then((result) => {
        // null means the operator backed out at the prompt, so say nothing and
        // leave the view exactly as it was.
        if (result) onDeleted(result);
      })
      .catch((err) => {
        // A plain reset refused because some run on the number is still
        // dialling - possibly not this one. Offer the force reset rather than
        // replacing the page with the refusal.
        if (scope === 'line' && err.response?.status === 409) {
          setDiallerError(
            `${err.response?.data?.detail || 'This line is still dialling'}. Use "Force reset" to stop the calls and reset anyway.`);
          setForceOffered(true);
          return;
        }
        setError(err.response?.data?.detail || 'Could not delete this run');
      })
      .finally(() => setDeleting(null));
  };
  const showForce = forceOffered || diallerActive || (run?.calls_in_flight || 0) > 0;

  if (loading && !run) return <LoadingSpinner size="lg" text="Loading the run..." />;
  if (error) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Back to runs
        </button>
        <div className="rounded-lg border border-border/60 bg-card/80 p-6 text-sm text-muted-foreground">{error}</div>
      </div>
    );
  }

  const agg = run.aggregates || {};
  const calls = run.calls || [];

  return (
    // Hold the previous render while refetching rather than flashing a skeleton.
    <div className={`space-y-6 transition-opacity ${loading ? 'opacity-60' : 'opacity-100'}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <button onClick={onBack} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-3">
            <ArrowLeft className="w-4 h-4" /> Back to runs
          </button>
          <h2 className="text-2xl font-semibold text-foreground">
            {run.label || run.target_number}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {run.target_number} · {run.completed_calls} call{run.completed_calls === 1 ? '' : 's'} ·{' '}
            {run.status}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <a href={ivrQa.exportUrl(run.id)} target="_blank" rel="noreferrer"
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-gradient-button text-primary-foreground shadow-md hover:opacity-90 transition-opacity">
            <Download className="w-4 h-4" /> Export CSV
          </a>
          {/* Destructive pair last, and quieter than the actions above them.
              Reset carries the map count in its tooltip because that is the
              thing being thrown away and it is not visible from the label. */}
          <button onClick={() => remove('run')} disabled={!!deleting}
            title="Delete this run and its calls. The explored menu map is kept."
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-border/60 text-muted-foreground hover:text-destructive hover:border-destructive/40 hover:bg-destructive/5 transition-colors disabled:opacity-50">
            {deleting === 'run'
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Trash2 className="w-4 h-4" />} Delete run
          </button>
          <button onClick={() => remove('line')} disabled={!!deleting}
            title={`Delete every run, call and explored menu for ${run.target_number}, so the next call starts from the root`}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-destructive/40 text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50">
            {deleting === 'line'
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <RotateCcw className="w-4 h-4" />} Reset line
          </button>
          {showForce && (
            <button onClick={() => remove('force')} disabled={!!deleting}
              title={`Stop all dialling to ${run.target_number}, hang up its queued and live calls, then reset the line`}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-destructive text-destructive-foreground hover:opacity-90 transition-opacity disabled:opacity-50">
              {deleting === 'force'
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <AlertTriangle className="w-4 h-4" />} Force reset
            </button>
          )}
        </div>
      </div>

      {/* The controls come before the report: on a run with no calls yet, this
          is the only thing on the page worth reading. */}
      <DiallerPanel
        run={run}
        error={diallerError}
        onError={setDiallerError}
        onChanged={(updated) => setRun((prev) => ({ ...prev, ...updated }))}
      />

      {/* The one hero per view. */}
      <HeroShare
        pct={agg.listening_share_pct}
        recordingS={agg.total_recording_time_s ?? null}
        totalS={agg.total_measured_time_s ?? null}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        <StatTile icon={PhoneCall} label="Calls placed" value={agg.call_count ?? 0} />
        <StatTile icon={User} label="Reached a person"
          value={`${agg.reached_human_count ?? 0} of ${agg.call_count ?? 0}`} />
        <StatTile icon={Clock} label="Median time to a person"
          value={agg.median_time_to_human_s !== null && agg.median_time_to_human_s !== undefined
            ? `${agg.median_time_to_human_s}s` : '--'}
          note={agg.fastest_time_to_human_s !== null && agg.fastest_time_to_human_s !== undefined
            ? `${agg.fastest_time_to_human_s}s fastest, ${agg.slowest_time_to_human_s}s slowest` : undefined} />
        <StatTile icon={Route} label="Distinct routes taken" value={agg.distinct_paths ?? 0} />
        <StatTile icon={Repeat} label="Loops found" value={agg.loop_count ?? 0}
          note={agg.stale_count ? `${agg.stale_count} stale announcement${agg.stale_count === 1 ? '' : 's'}` : undefined} />
      </div>

      {/* The hero gives the share as one number; this gives the split behind it
          in absolute time. The aggregate reports seconds, the bar takes
          milliseconds. */}
      {/* Silence comes from the API as a reported figure, not derived here as
          duration minus speech - that derivation swept up ring time and
          teardown and disagreed with the call view about the same seconds. */}
      {agg.total_measured_time_s > 0 && (
        <CompositionBar
          recordingMs={(agg.total_recording_time_s || 0) * 1000}
          silenceMs={(agg.total_silence_time_s || 0) * 1000}
          subtitle={`Every second spent on this line across ${agg.call_count} call${agg.call_count === 1 ? '' : 's'}`}
        />
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <TimeToHumanChart calls={calls} onSelect={(c) => onSelectCall(c.id)} />
        <OutcomeBreakdown outcomes={agg.outcomes} total={agg.call_count ?? 0} />
      </div>

      <FindingsPanel calls={calls} />

      <TreeView tree={tree} loading={treeLoading} />

      {/* Table view: every call, as text. */}
      <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
        <h3 className="text-lg font-semibold text-foreground mb-1">Every call in this run</h3>
        <p className="text-sm text-muted-foreground mb-5">Select a call to see it menu by menu</p>
        {!calls.length ? (
          <p className="text-sm text-muted-foreground">
            No calls recorded against this run yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border/60">
                  <th className="pb-2 pr-3 font-medium">#</th>
                  <th className="pb-2 pr-3 font-medium">Route taken</th>
                  <th className="pb-2 pr-3 font-medium">Ended, and why</th>
                  <th className="pb-2 pr-3 font-medium text-right">To a person</th>
                  <th className="pb-2 pr-3 font-medium text-right">Length</th>
                  <th className="pb-2 pr-3 font-medium text-right">Menus</th>
                  <th className="pb-2 font-medium text-right">Issues</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((call) => (
                  <tr key={call.id}
                    onClick={() => onSelectCall(call.id)}
                    className="border-b border-border/40 last:border-0 cursor-pointer hover:bg-muted/30 transition-colors">
                    <td className="py-3 pr-3 tabular-nums text-muted-foreground">{call.call_index}</td>
                    <td className="py-3 pr-3 text-foreground">
                      <span className="tabular-nums">
                        {(call.path || []).length ? (call.path || []).join(' → ') : '--'}
                      </span>
                    </td>
                    {/* The label is the condition; the line under it is what
                        happened. Without the second line every row here read
                        "Agent ended it", which tells a client nothing. */}
                    <td className="py-3 pr-3 text-muted-foreground max-w-sm">
                      <div className="whitespace-nowrap">{outcomeLabel(call.outcome)}</div>
                      {call.end_reason && (
                        <div className="text-xs mt-0.5 line-clamp-2">{call.end_reason}</div>
                      )}
                    </td>
                    <td className="py-3 pr-3 text-right tabular-nums whitespace-nowrap">
                      {call.time_to_human_ms !== null && call.time_to_human_ms !== undefined ? (
                        <span className="text-foreground">{fmtSecondsOrDash(call.time_to_human_ms)}</span>
                      ) : (
                        <span className="text-muted-foreground">never</span>
                      )}
                    </td>
                    <td className="py-3 pr-3 text-right tabular-nums text-muted-foreground whitespace-nowrap">
                      {fmtSecondsOrDash(call.total_duration_ms)}
                      {call.ring_to_answer_ms ? (
                        <div className="text-xs">rang {fmtSecondsOrDash(call.ring_to_answer_ms)}</div>
                      ) : null}
                    </td>
                    <td className="py-3 pr-3 text-right tabular-nums text-muted-foreground">
                      {call.turn_count ?? 0}
                    </td>
                    <td className="py-3 text-right tabular-nums text-muted-foreground">
                      {visibleIssues(call).length || ''}
                      <ChevronRight className="w-4 h-4 inline-block ml-2 text-muted-foreground" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Runs list
// ─────────────────────────────────────────────────────────────────────────────

const RunsList = ({ onSelectRun }) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newNumber, setNewNumber] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');
  // Opening a run and dialling it are two decisions, so dialling is opt in: a
  // run opened to collect calls placed by hand is still a normal thing to want.
  const [autoStart, setAutoStart] = useState(false);
  const { options: callerIds } = useCallerIds();
  const [dialler, setDialler] = useState({
    planned_calls: 10,
    from_number: '',
    pacing_seconds: 90,
    max_concurrency: '1',
  });

  useEffect(() => {
    if (dialler.from_number || !callerIds.length) return;
    const ready = callerIds.find((c) => c.qaReady);
    if (ready) setDialler((d) => ({ ...d, from_number: ready.e164 }));
  }, [callerIds, dialler.from_number]);

  const load = useCallback(() => {
    setLoading(true);
    ivrQa.listRuns()
      .then((res) => { setRuns(res.data.runs || []); setError(null); })
      .catch((err) => setError(err.response?.data?.detail || 'Could not load runs'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Keep the list honest while a campaign is running somewhere: the counts on
  // these rows move on their own.
  const anyDialling = runs.some((r) => DIALLER_ACTIVE.includes(r.orchestrator_status));
  useEffect(() => {
    if (!anyDialling) return undefined;
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [anyDialling, load]);

  const submit = (e) => {
    e.preventDefault();
    const target = newNumber.trim();
    if (!target) return;
    setSaving(true);
    ivrQa.createRun({
      target_number: target,
      label: newLabel.trim() || null,
      // Sent whether or not the dialler starts, so the settings are already
      // right when someone starts it from the run page later.
      planned_calls: Number(dialler.planned_calls) || 0,
      from_number: dialler.from_number || null,
      pacing_seconds: Number(dialler.pacing_seconds) || 90,
      max_concurrency: Number(dialler.max_concurrency) || 1,
      auto_start: autoStart,
    })
      .then((res) => {
        setCreating(false);
        setNewNumber('');
        setNewLabel('');
        load();
        if (res.data?.id) onSelectRun(res.data.id);
      })
      .catch((err) => setError(err.response?.data?.detail || 'Could not start the run'))
      .finally(() => setSaving(false));
  };

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return runs;
    return runs.filter((r) =>
      (r.target_number || '').toLowerCase().includes(q) ||
      (r.label || '').toLowerCase().includes(q));
  }, [runs, filter]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">IVR Quality Assurance</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            What is actually inside a phone tree: how long it takes a caller to reach a
            person, where it loops, and how much of the call is spent listening to
            recordings. One run is a campaign of calls against one line.
          </p>
        </div>
        <button onClick={() => setCreating((v) => !v)}
          className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-gradient-button text-primary-foreground shadow-md hover:opacity-90 transition-opacity flex-shrink-0">
          <PhoneCall className="w-4 h-4" /> Start a run
        </button>
      </div>

      {creating && (
        <form onSubmit={submit}
          className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Start a run</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Opening a run for a line closes any run already open on it, so new calls are
              attributed to this one. Point an agent with IVR QA mode on at the number, and
              space the calls out.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground">Number to audit</label>
              <input value={newNumber} onChange={(e) => setNewNumber(e.target.value)}
                placeholder="+18004598105" required
                className="mt-1 w-full px-3 py-2 text-sm bg-background border border-border/60 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
              <p className="text-xs text-muted-foreground mt-1">E.164, the same form the agent dials</p>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Name (optional)</label>
              <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Crown Cruise main line"
                className="mt-1 w-full px-3 py-2 text-sm bg-background border border-border/60 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
            </div>
          </div>
          <label className="flex items-start gap-3 rounded-lg border border-border/60 bg-muted/20 p-4 cursor-pointer">
            <input type="checkbox" checked={autoStart}
              onChange={(e) => setAutoStart(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-border/60 accent-primary" />
            <span className="text-sm">
              <span className="font-medium text-foreground">Place the calls for me</span>
              <span className="block text-muted-foreground mt-0.5">
                The dialler works through the campaign on its own, leaving a gap after each
                call. Leave this off to place the calls by hand and have them recorded
                against this run.
              </span>
            </span>
          </label>

          {autoStart && (
            <DiallerSettings value={dialler} onChange={setDialler}
              callerIds={callerIds} disabled={saving} />
          )}

          <div className="flex items-center gap-2">
            <button type="submit"
              disabled={saving || !newNumber.trim()
                        || (autoStart && !Number(dialler.planned_calls))}
              className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-gradient-button text-primary-foreground shadow-md hover:opacity-90 transition-opacity disabled:opacity-50">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {autoStart ? 'Start run and dial' : 'Start run'}
            </button>
            <button type="button" onClick={() => setCreating(false)}
              className="px-4 py-2 text-sm rounded-lg border border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors">
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* One filter row, above everything it scopes. */}
      {runs.length > 3 && (
        <div className="relative max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by number or name"
            className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border/60 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40" />
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-border/60 bg-card/80 p-4 text-sm text-muted-foreground">{error}</div>
      )}

      {loading ? (
        <LoadingSpinner size="lg" text="Loading runs..." />
      ) : !visible.length ? (
        <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-10 text-center shadow-elegant">
          <PhoneCall className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-foreground">
            {runs.length ? 'Nothing matches that filter' : 'No runs yet'}
          </h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            {runs.length
              ? 'Clear the filter to see every run.'
              : 'Start a run against a number, then point an agent with IVR QA mode on at it. Each call maps a bit more of the tree.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((run) => (
            <button key={run.id} onClick={() => onSelectRun(run.id)}
              className="w-full text-left rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-5 shadow-elegant hover:border-primary/30 hover:shadow-lg transition-all">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-medium text-foreground truncate">
                    {run.label || run.target_number}
                  </div>
                  <div className="text-sm text-muted-foreground mt-0.5">
                    {run.target_number} · {run.completed_calls} call
                    {run.completed_calls === 1 ? '' : 's'}
                    {run.started_at ? ` · started ${fmtWhen(run.started_at)}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {DIALLER_ACTIVE.includes(run.orchestrator_status) && (
                    <span className="text-xs px-2 py-1 rounded bg-primary/15 text-primary tabular-nums">
                      {DIALLER_LABELS[run.orchestrator_status]}
                      {run.planned_calls
                        ? ` ${run.attempted_calls || 0}/${run.planned_calls}`
                        : ''}
                    </span>
                  )}
                  {run.orchestrator_status === 'failed' && (
                    <span className="text-xs px-2 py-1 rounded bg-destructive/10 text-destructive">
                      Dialler stopped on an error
                    </span>
                  )}
                  <span className={`text-xs px-2 py-1 rounded ${
                    run.status === 'active'
                      ? 'bg-primary/15 text-primary'
                      : 'bg-muted/50 text-muted-foreground'
                  }`}>
                    {run.status}
                  </span>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Container
// ─────────────────────────────────────────────────────────────────────────────

const IvrQaDashboard = () => {
  const [runId, setRunId] = useState(null);
  const [callId, setCallId] = useState(null);
  const [notice, setNotice] = useState(null);

  // What a delete actually removed, reported once on the list it returns to.
  // The counts are the confirmation that it did what the prompt described -
  // "0 menus" after a reset would mean the map was not the one being looked at.
  const handleDeleted = (result) => {
    setRunId(null);
    setCallId(null);
    setNotice(
      result.exploration_reset
        ? `Reset ${result.target_number}: removed ${result.runs_deleted} run(s), `
          + `${result.calls_deleted} call(s) and ${result.nodes_deleted} explored menu(s). `
          + 'The next call starts from the root.'
        : `Deleted "${result.label}": ${result.calls_deleted} call(s) removed. `
          + `The explored map for ${result.target_number} was kept, so the next call resumes.`,
    );
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-4">
      {notice && !runId && !callId && (
        <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-card/80 backdrop-blur-sm p-4 text-sm text-muted-foreground">
          <Trash2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{notice}</span>
          <button onClick={() => setNotice(null)}
            className="text-muted-foreground hover:text-foreground flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {callId ? (
        <CallDetail callId={callId} onBack={() => setCallId(null)} />
      ) : runId ? (
        <RunDetail runId={runId} onBack={() => setRunId(null)}
          onSelectCall={setCallId} onDeleted={handleDeleted} />
      ) : (
        <RunsList onSelectRun={(id) => { setNotice(null); setRunId(id); }} />
      )}
    </div>
  );
};

export default IvrQaDashboard;
